import { computed, onBeforeUnmount, ref, watch, type Ref } from "vue";
import { DS_TOOLTIP_PLACEMENTS, type DSTooltipOptions } from "@/src/design_system/primitives/tooltip/types/options";
import { COPY_FEEDBACK_TIMEOUT_MS } from "@/src/shared/constants/clipboard";
import { copyToClipboard } from "@/src/utils/clipboard";

interface UseDSCopyButtonOptions {
  value: Ref<string>;
  disabled: Ref<boolean>;
  ariaLabel: Ref<string | undefined>;
  tooltip: Ref<string | undefined>;
}

export const useDSCopyButton = ({ value, disabled, ariaLabel, tooltip: tooltipText }: UseDSCopyButtonOptions) => {
  const copiedValue = ref<string>();
  let feedbackTimeout: ReturnType<typeof setTimeout> | undefined;
  let latestCopyOperation = 0;
  let isUnmounted = false;

  const isCopied = computed(() => copiedValue.value !== undefined && copiedValue.value === value.value);
  const isDisabled = computed(() => disabled.value || !value.value);
  const buttonIcon = computed(() => (isCopied.value ? "done" : "copy"));
  const buttonLabel = computed(() => ariaLabel.value || window.I18n.buttonCopy);
  const tooltip = computed<DSTooltipOptions | false>(() => {
    if (!tooltipText.value?.trim() || isDisabled.value) return false;

    return {
      text: tooltipText.value,
      placement: DS_TOOLTIP_PLACEMENTS.Top,
    };
  });

  const resetFeedback = () => {
    clearTimeout(feedbackTimeout);
    feedbackTimeout = undefined;
    copiedValue.value = undefined;
  };

  const invalidateCopyOperation = () => {
    latestCopyOperation += 1;
    resetFeedback();
  };

  const isStaleOperation = (operation: number, succeeded: boolean, copied: string) => {
    if (operation === latestCopyOperation) return false;
    if (succeeded && !isUnmounted && copiedValue.value !== copied) resetFeedback();

    return true;
  };

  const canShowFeedback = (succeeded: boolean, copied: string) =>
    succeeded && !isUnmounted && value.value === copied && !disabled.value;

  const showFeedback = (copied: string) => {
    resetFeedback();
    copiedValue.value = copied;
    feedbackTimeout = setTimeout(resetFeedback, COPY_FEEDBACK_TIMEOUT_MS);
  };

  const copyValue = async () => {
    if (isDisabled.value) return;

    const copied = value.value;
    const operation = ++latestCopyOperation;
    const succeeded = await copyToClipboard(copied, { noFlash: true });
    if (isStaleOperation(operation, succeeded, copied)) return;
    if (!canShowFeedback(succeeded, copied)) return;

    showFeedback(copied);
  };

  watch([value, disabled], invalidateCopyOperation);

  onBeforeUnmount(() => {
    isUnmounted = true;
    invalidateCopyOperation();
  });

  return {
    buttonIcon,
    buttonLabel,
    copyValue,
    isDisabled,
    tooltip,
  };
};
